/* eslint react/prop-types: 0 */
import {
  CopyAllOutlined,
  FavoriteBorderOutlined,
  LaunchOutlined,
  LinkOutlined,
  MoreVert,
  PlayCircleOutlineOutlined,
  SearchOutlined,
} from '@mui/icons-material';
import type { Theme } from '@mui/material';
import { Tooltip } from '@mui/material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from '@mui/material';
import { Box, Button, styled } from '@mui/material';
import type { Creative } from '../types';
import { format, intervalToDuration } from 'date-fns';
import theme from '../../../theme';
import { formatDuration } from '../utils';
import { memo } from 'react';
import type { ElementType, FC, ReactNode } from 'react';
import { useMemo, useRef, useState } from 'react';
import { useCallback } from 'react';
import copy from 'copy-to-clipboard';
import useEnqueueSnackbar from '../../../hooks/useEnqueueSnackbar';
import { useAnchorMenu } from '../../../hooks';
import type { TypeText } from '@mui/material/styles/createPalette';
import type { SystemStyleObject } from '@mui/system';
import Carousel from './Carousel';
import type { CreativeAdType } from '../api/types';
import PreviewIcon from '@mui/icons-material/Preview';
interface PreviewComponentProps {
  src: string | string[];
  alt?: string;
  title?: string;
}

export const PreviewContainer = styled(Box)<{ img: string }>(({ img, theme }: { img: string; theme: Theme }) => ({
  marginTop: theme.spacing(2),
  aspectRatio: '1',
  background: `url('${img}')`,
  backgroundSize: 'cover',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  maxWidth: 230,
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    inset: 0,
    backdropFilter: 'blur(10px)',
  },
}));

export const PreviewImage = styled('img')(({ theme: t }) => ({
  objectFit: 'contain',
  width: '100%',
  height: '100%',
  margin: 0,
  padding: 0,
  borderRadius: t.shape.borderRadius,
}));

export const PreviewIframe = styled('iframe')({
  objectFit: 'contain',
  width: '100%',
  height: '100%',
  position: 'relative',
});

export const PreviewActionsArea = styled(Box)(({ theme }: { theme: Theme }) => ({
  position: 'absolute',
  top: 0,
  right: 0,
  display: 'flex',
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  zIndex: 2,
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
}));

export const CreativePaper = styled(Paper)(({ theme }) => ({
  fontFamily: 'Inter Tight, sans-serif',
  display: 'flex',
  flexDirection: 'column',
  padding: 0,
  margin: 0,
  textAlign: 'center',
  color: theme.palette.text.secondary,
  ...theme.applyStyles('dark', {
    backgroundColor: '#1A2027',
  }),
  fontSize: 0,
  backgroundColor: '#fff',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  '& .creative-nav-panel': { visibility: 'hidden', opacity: 0, transform: 'translateY(10px)', transition: 'all 0.1s' },
  '&:hover .creative-nav-panel, &.visibility .creative-nav-panel': {
    visibility: 'visible',
    opacity: 1,
    transform: 'translateY(0px)',
  },
  '& .play-button': {
    backgroundColor: 'rgba(0,0,0,0.45)',
  },
  '&:hover .play-button, &.visibility .play-button': {
    backgroundColor: 'rgba(0,0,0,0.7)',
    width: '105px',
  },
  '& .play-button .duration': {
    opacity: '0',
    transform: 'translateX(-40px)',
    transition: 'all 0.2s',
  },
  '&:hover .play-button .duration, &.visibility .play-button .duration': {
    transform: 'translateX(0px)',
    opacity: 1,
  },
}));

export const CreativeAppName = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.body2.fontSize,
  color: theme.palette.text.primary,
  textAlign: 'left',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  width: '90%',
  flex: 1,
  overflow: 'hidden',
}));

const CreativeMetricWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'nowrap',
  gap: theme.spacing(0.75),
});

const CreativeMetricTypography = styled(Typography)(({ theme: t }) => ({
  color: t.palette.primary.light,
  fontSize: t.typography.caption.fontSize,
  fontWeight: 500,
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  display: 'inline-block',
  overflow: 'hidden',
}));

interface CreativeMetricProps {
  title?: string;
  type: 'likes' | 'interval' | 'reach';
  value: string | number | Date;
}
export const CreativeMetric = memo(({ type, value, title }: CreativeMetricProps) => {
  if (type === 'reach') {
    return (
      <Tooltip title={title ?? 'Reach'} placement={'bottom'}>
        <CreativeMetricWrapper>
          <PreviewIcon sx={(t) => ({ color: t.palette.primary.light, fontSize: t.typography.body2.fontSize })} />
          <CreativeMetricTypography>{value as string}</CreativeMetricTypography>
        </CreativeMetricWrapper>
      </Tooltip>
    );
  }

  if (type === 'likes') {
    return (
      <Tooltip title={title ?? 'Impressions'} placement={'bottom'}>
        <CreativeMetricWrapper>
          <FavoriteBorderOutlined
            sx={(t) => ({ color: t.palette.primary.light, fontSize: t.typography.body2.fontSize })}
          />
          <CreativeMetricTypography>{value as string}</CreativeMetricTypography>
        </CreativeMetricWrapper>
      </Tooltip>
    );
  }

  const intervalDays = intervalToDuration({ start: new Date(value), end: new Date() }).days;
  return (
    <Tooltip title={title ?? 'In WellSpy'} placement={'bottom'}>
      <CreativeMetricWrapper sx={{ gap: 0.25 }}>
        <SearchOutlined sx={(t) => ({ color: t.palette.primary.light, fontSize: t.typography.body2.fontSize })} />
        <CreativeMetricTypography>{intervalDays} days</CreativeMetricTypography>
      </CreativeMetricWrapper>
    </Tooltip>
  );
});

CreativeMetric.displayName = 'CreativeMetric';

export const CreativeAccordion = styled(Accordion)(({ theme: t }) => ({
  '&': { alignItems: 'start', justifyContent: 'start', width: '100%', gap: t.spacing(1) },
  '&.Mui-expanded': { margin: 0, minHeight: 24 },
  padding: 0,
  boxShadow: 'none',
  border: 'none',
  '&:before': { display: 'none' },
}));

export const CreativeAccordionSummary = styled(AccordionSummary)({
  '&': { display: 'flex', minHeight: '0', height: 24, padding: 0 },
  '&.Mui-expanded': { margin: 0, minHeight: 24 },
  '& .MuiAccordionSummary-content': {
    gap: 8,
    margin: 0,
  },
});

export const CreativeAccordionDetails = styled(AccordionDetails)(({ theme: t }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  alignItems: 'start',
  justifyContent: 'start',
  padding: '8px 0 0',
  boxShadow: 'none',
  border: 'none',
  gap: t.spacing(1),
}));

export const CreativeDateTypography: FC<{ children: ReactNode; color: TypeText | string; sx?: SystemStyleObject<Theme> }> = memo(
  ({ children, color, sx }) => {
    return (
      <Typography
        component={'span'}
        variant={'caption'}
        sx={(t) => ({
          lineHeight: '1.2',
          color: color === 'secondary' ? t.palette.text.secondary : t.palette.text.primary,
          ...sx,
        })}
      >
        {children}
      </Typography>
    );
  }
);
CreativeDateTypography.displayName = 'CreativeDateTypography';

export const CreativeDetails: FC<{ title: string; textHtml: string }> = memo(({ title, textHtml }) => {
  return (
    <Box>
      <Typography
        sx={(t) => ({
          fontSize: t.typography.body2.fontSize,
          color: t.palette.text.primary,
          textAlign: 'left',
        })}
      >
        {title}
      </Typography>
      <Typography
        sx={(t) => ({
          fontSize: t.typography.caption.fontSize,
          color: t.palette.text.secondary,
          textAlign: 'left',
        })}
        dangerouslySetInnerHTML={{ __html: textHtml }}
      ></Typography>
    </Box>
  );
});
CreativeDetails.displayName = 'CreativeDetails';

export const CreativeDateSeen: FC<{ firstSeen: Date; lastSeen: Date }> = memo(({ firstSeen, lastSeen }) => {
  return (
    <Box
      sx={(t) => ({
        pb: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'start',
        justifyContent: 'start',
        borderBottom: `1px solid ${t.palette.divider}`,
        width: '100%',
      })}
    >
      <Box sx={{ display: 'flex', gap: 1 / 2 }}>
        <CreativeDateTypography color={'secondary'}>First seen:</CreativeDateTypography>
        <CreativeDateTypography color={'primary'}>{format(firstSeen, 'dd.MM.yyyy')}</CreativeDateTypography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 / 2 }}>
        <CreativeDateTypography color={'secondary'}>Last seen:</CreativeDateTypography>
        <CreativeDateTypography color={'primary'}>{format(lastSeen, 'dd.MM.yyyy')}</CreativeDateTypography>
      </Box>
    </Box>
  );
});
CreativeDateSeen.displayName = 'CreativeDetails';

export const CreativeAppIosLink: FC<{ id: string }> = memo(({ id }) => {
  const { onShowAlert } = useEnqueueSnackbar();

  const handleCopyAppUrl = useCallback(() => {
    copy(`https://apps.apple.com/app/id${id}`);
    onShowAlert(Date.now(), 'App URL was copied to clipboard!');
  }, [id, onShowAlert]);

  return (
    <Button variant={'text'} sx={{ p: 0, textTransform: 'initial' }}>
      <Typography
        onClick={handleCopyAppUrl}
        variant={'caption'}
        sx={(t) => ({
          display: 'flex',
          gap: 1 / 2,
          alignItems: 'center',
          color: t.palette.text.disabled,
          wordBreak: 'break-all',
          textAlign: 'left',
          lineHeight: '1.25',
        })}
      >
        <LinkOutlined sx={(t) => ({ fontSize: 18, color: t.palette.text.disabled })} />
        {`https://apps.apple.com/app/id${id}`}
      </Typography>
    </Button>
  );
});
CreativeAppIosLink.displayName = 'CreativeDetails';

export const CreativePlayIconButton = styled(IconButton)(() => ({
  height: '56px',
  borderRadius: 28,
  position: 'absolute',
  zIndex: 2,
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  padding: '0 12px 0',
  margin: 'auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'start',
  flexWrap: 'nowrap',
  width: '56px',
  transition: 'all 0.2s',
  overflow: 'hidden',
}));

export const CreativePlayVideoButton: FC<{ duration: number; onClick: () => void }> = memo(({ duration, onClick }) => {
  return (
    <CreativePlayIconButton className={'play-button'} onClick={onClick}>
      <PlayCircleOutlineOutlined sx={{ color: 'white', fontSize: '32px' }} />
      <Typography className={'duration'} sx={(t) => ({ color: 'white', fontSize: t.typography.body1, pl: 1 / 2 })}>
        {formatDuration(duration)}
      </Typography>
    </CreativePlayIconButton>
  );
});
CreativePlayVideoButton.displayName = 'CreativeDetails';

export const CreativeNavPanel: FC<{ creative: Creative; handleExpansionCommentTags?: () => void }> = memo(
  ({ creative, handleExpansionCommentTags }) => {
    const { anchor, handleOpenMenu, handleCloseMenu, isOpen } = useAnchorMenu();
    const isVideo = creative.ad_type === 'video';
    const { onShowAlert } = useEnqueueSnackbar();

    const handleTogglePanelVisibility = useCallback(() => {
      const dialogContainer = document.querySelector(`#creative-${creative.id}-dialog`);
      if (dialogContainer) {
        dialogContainer.classList.toggle('visibility');
        return;
      }

      const paperContainer = document.querySelector(`#creative-${creative.id}-paper`);
      paperContainer?.classList.toggle('visibility');
    }, [creative.id]);

    const handleToggleMenu: React.MouseEventHandler<HTMLElement> = (event) => {
      handleTogglePanelVisibility();
      isOpen ? handleCloseMenu() : handleOpenMenu(event);
    };

    const handleCopyCreoUrl = useCallback(() => {
      copy(isVideo ? creative.vid_link : creative.img_link);
      onShowAlert(creative.id, 'Creo URL was copied to clipboard!');
    }, [creative.id, creative.img_link, creative.vid_link, isVideo, onShowAlert]);

    const handleCopyPreviewUrl = useCallback(() => {
      copy(isVideo ? creative.vid_link : creative.img_link);
      onShowAlert(creative.id, 'Preview URL was copied to clipboard!');
      handleCloseMenu();
      handleTogglePanelVisibility();
    }, [
      creative.id,
      creative.img_link,
      creative.vid_link,
      isVideo,
      handleCloseMenu,
      handleTogglePanelVisibility,
      onShowAlert,
    ]);

    const handleCommentTags = useCallback(() => {
      handleCloseMenu();
      handleExpansionCommentTags?.();
    }, [handleCloseMenu, handleExpansionCommentTags]);

    const handleOpenUrl = useCallback(() => {
      window.open(isVideo ? creative.vid_link : creative.img_link, '_blank');
    }, [creative.img_link, creative.vid_link, isVideo]);

    return (
      <Box
        className={'creative-nav-panel'}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          px: 1,
          position: 'absolute',
          bottom: 6,
          left: 6,
          right: 6,
          height: 42,
          margin: 'auto',
          zIndex: 2,
          backgroundColor: 'rgba(0,0,0, 0.5)',
          borderRadius: '4px',
        }}
      >
        <Button
          onClick={handleCopyCreoUrl}
          variant={'text'}
          size={'small'}
          startIcon={<CopyAllOutlined />}
          sx={{ color: 'white', lineHeight: '1', textAlign: 'left' }}
        >
          Creo url
        </Button>
        <Button
          onClick={handleOpenUrl}
          variant={'text'}
          size={'small'}
          startIcon={<LaunchOutlined />}
          sx={{ color: 'white' }}
        >
          Open
        </Button>
        <IconButton
          sx={{ p: 0 }}
          id={`creative-${creative.id}-nav-button`}
          aria-controls={isOpen ? `creative-${creative.id}-nav-menu` : undefined}
          aria-haspopup="true"
          aria-expanded={isOpen ? 'true' : undefined}
          onClick={handleToggleMenu}
        >
          <MoreVert sx={{ color: 'white' }} />
        </IconButton>
        <Menu
          id={`creative-${creative.id}-nav-menu`}
          anchorEl={anchor}
          open={isOpen}
          onClose={handleToggleMenu}
          MenuListProps={{
            'aria-labelledby': `creative-${creative.id}-nav-button`,
          }}
        >
          <MenuItem onClick={handleCopyPreviewUrl}>Copy preview URL</MenuItem>
          {handleExpansionCommentTags && <MenuItem onClick={handleCommentTags}>Comment and tags</MenuItem>}
        </Menu>
      </Box>
    );
  }
);
CreativeNavPanel.displayName = 'CreativeNavPanel';

export const PreviewCarousel: FC<PreviewComponentProps> = memo(({ src }) => {
  return (
    <Box sx={{ position: 'relative', width: '100%', height: 'auto' }}>
      <Box component={Carousel} images={src as string[]} sx={{ width: '100%', height: 'auto' }} />
    </Box>
  );
});
PreviewCarousel.displayName = 'PreviewCarousel';

export const CreativePreview: FC<{ creative: Creative }> = memo(({ creative }) => {
  const [isShowVideoPreview, setShowVideoPreview] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isVideo = creative.ad_type === 'video';

  interface CreativePreviewConfig {
    component: ElementType;
    props: Record<string, string | string[]>;
  }

  const CreativePreviewMap: Record<
    CreativeAdType,
    CreativePreviewConfig
  > = useMemo(
    () => ({
      video: { component: PreviewImage, props: { src: creative.img_link, alt: 'preview' } },
      image: { component: PreviewImage, props: { src: creative.img_link, alt: 'preview' } },
      carousel: { component: PreviewCarousel, props: { src: creative.resources } },
      html: { component: PreviewIframe, props: { src: creative.resources[0], title: creative.img_link } },
    }),
    [creative.img_link, creative.resources]
  );

  const CreativePreviewRender = CreativePreviewMap[creative.ad_type].component;
  const previewRenderProps = CreativePreviewMap[creative.ad_type].props;

  const handleToggleVideo = useCallback(() => {
    setShowVideoPreview((prev) => !prev);
  }, [setShowVideoPreview]);

  return (
    <>
      {isVideo && isShowVideoPreview && (
        <Box
          ref={videoRef}
          sx={{ position: 'absolute', width: '100%', height: '100%', zIndex: 3, background: '#000' }}
          component="video"
          autoPlay
          controls
          muted
          onClick={handleToggleVideo}
        >
          <source src={creative.vid_link} type="video/mp4" />
        </Box>
      )}
      {isVideo && <CreativePlayVideoButton duration={creative.video_duration} onClick={handleToggleVideo} />}
      <CreativePreviewRender {...previewRenderProps} />
    </>
  );
});
CreativePreview.displayName = 'CreativePreview';
